import { ToolOutlined } from '@ant-design/icons';

import { Badge, Button, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import type { LuotApSua } from '../types';

export interface HopApSuaProps {
  luot: LuotApSua | undefined;
  dangTai: boolean;
  loi: unknown;
  /** Số gợi ý người thẩm định đã đánh dấu đồng ý. */
  soDongY: number;
  duocApDung: boolean;
  dangApDung: boolean;
  onApDung: () => void;
  onMoHoSoMoi: (phienId: string) => void;
  /** Tên tiêu chí theo mã, để danh sách bỏ qua đọc được. */
  tenTheoMa: Record<string, string>;
}

/**
 * Áp gợi ý sửa: dựng lại hồ sơ theo các gợi ý đã đồng ý rồi chấm lại (đặc tả mục 11.2, Mốc 12).
 *
 * Quyết định trình bày quan trọng nhất: **danh sách thao tác bị bỏ phải hiện trên màn hình**, không nằm
 * trong log. Đặc tả mục 2.5 nói rõ vì sao — *"Nuốt một thao tác không áp được tệ hơn hẳn việc báo 'không áp
 * được 3 chỗ': người dùng tin hồ sơ đã được sửa đủ trong khi chưa."*
 *
 * Và khối ấy nằm **cùng chỗ** với liên kết mở hồ sơ mới, không ở một tab khác: người dùng vừa thấy "đã dựng
 * lần nộp mới" là lúc họ cần biết ba chỗ đã không sửa được.
 */
export function HopApSua({
  luot, dangTai, loi, soDongY, duocApDung, dangApDung, onApDung, onMoHoSoMoi, tenTheoMa,
}: HopApSuaProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();

  if (dangTai) return <Skeleton className="h-16 w-full" />;
  if (loi) {
    return (
      <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{dichLoi(loi)}</p>
    );
  }

  const dangChay = luot ? ['cho', 'dang_chay'].includes(luot.trang_thai) : false;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ToolOutlined className="shrink-0 text-primary-600" />
        <p className="min-w-0 flex-1 text-sm text-content-muted">{t('thamDinh.raSoat.apSuaMo')}</p>
      </div>

      {dangChay && (
        <div className="flex items-center gap-2 rounded border border-primary-200 bg-primary-50 p-3">
          <span className="size-2 animate-pulse rounded-full bg-primary-500" aria-hidden="true" />
          <p className="min-w-0 flex-1 truncate text-sm text-primary-800">
            {luot!.buoc || t('thamDinh.raSoat.apSuaDangChay')}
          </p>
        </div>
      )}

      {luot?.trang_thai === 'loi' && (
        <div className="rounded border border-danger-200 bg-danger-50 p-3">
          <p className="font-medium text-danger-800">{t('thamDinh.raSoat.apSuaLoi')}</p>
          <p className="mt-1 text-sm text-danger-700">{luot.loi}</p>
        </div>
      )}

      {luot?.trang_thai === 'xong' && (
        <div className="space-y-2 rounded border border-success-200 bg-success-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">{t('thamDinh.raSoat.apSuaXong')}</Badge>
            {luot.phien_moi_id && (
              <Button variant="link" size="sm" onClick={() => onMoHoSoMoi(luot.phien_moi_id!)}>
                {t('thamDinh.raSoat.apSuaMoHoSoMoi')}
              </Button>
            )}
          </div>

          {/*
            * Thao tác bị bỏ: hiện ngay cạnh "đã dựng lần nộp mới", không ở chỗ khác. Đặc tả mục 2.5 — người
            * dùng tin hồ sơ đã được sửa đủ trong khi chưa là lỗi nặng nhất của tính năng này.
            */}
          {luot.bo_qua.length > 0 && (
            <div className="rounded border border-warning-200 bg-warning-50 p-2">
              <p className="text-2xs font-medium text-warning-800">
                {t('thamDinh.raSoat.apSuaBoQua', { so: soVN(luot.bo_qua.length, locale) })}
              </p>
              <ul className="mt-1 space-y-1">
                {luot.bo_qua.map((b, i) => (
                  <li key={`${b.ma}-${i}`} className="text-sm text-warning-800">
                    <span className="font-medium">{tenTheoMa[b.ma] ?? b.ma}</span>: {b.ly_do}
                    {b.tim && (
                      <p className="truncate font-mono text-2xs text-content-muted">“{b.tim}”</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {duocApDung && !dangChay && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {soDongY === 0 ? (
            <p className="min-w-0 flex-1 text-sm text-content-muted">{t('thamDinh.raSoat.apSuaChuaDongY')}</p>
          ) : (
            <p className="min-w-0 flex-1 text-sm text-content-muted">
              {t('thamDinh.raSoat.apSuaSoDongY', { so: soVN(soDongY, locale) })}
            </p>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={<ToolOutlined />}
            loading={dangApDung}
            disabled={soDongY === 0}
            onClick={onApDung}
          >
            {t('thamDinh.raSoat.apSua')}
          </Button>
        </div>
      )}
    </div>
  );
}
